// src/pages/projects/ProjectMaterials.jsx

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "../../firebase";
import {
  fetchMaterialStages, createMaterialStage, deleteMaterialStage, updateMaterialStage,
  updateStageItems, fetchProjectMRFs, createProjectMRF, STAGE_STATUS, ALERT_LEVEL, MRF_STATUS,
} from "./projectMaterialService";
import { fetchBoqItems } from "./projectBoqItemsService";
import { canAccessProject } from "./projectAccess";
// NOTE: createReturnMRF is intentionally NOT imported here anymore — same
// reason as approveProjectMRF/rejectProjectMRF/markMRFIssued above. It only
// adjusted the stage's material_budget tracking and never called
// returnMaterial()/defectiveReturn() from inventoryService.js, so real
// inventory_summary stock was never actually restored. Returns now go
// through ReturnMaterial.js (src/roles/store/ReturnMaterial.js) — the
// single governed engine, which already reads from "material_requests"
// directly and works for project-stage MRFs with no changes needed.
// NOTE: approveProjectMRF / rejectProjectMRF / markMRFIssued are intentionally
// NOT imported here anymore. Approving/rejecting/issuing a material request
// must always go through StoreMRFDetail.js — the single, governed engine
// that calls issueAgainstMR() (real inventory_summary deduction +
// inventory_transactions ledger entry) and sets correct per-item
// item_status. This page previously had its own mini approve/issue engine
// that skipped that entirely — it changed the MRF's status to "issued"
// without ever touching real stock. See D-1XX in DECISION_LOG.
import { fetchChecklistItems } from "./projectService";
import {
  fetchActiveItems, generateNextItemId,
  createNewItem, fetchExistingStageNames, saveBOQStages,
} from "./boqUploadService";
import {
  matchList, resolveBoqRow, decideBoqRows, newNameClashes, matchReasonText, candidateLabel,
} from "../../utils/boqMatch";
import { itemOptionLabel } from "../../utils/itemPicker";
import AIBoqImportPanel from "../../components/AIBoqImportPanel";

// ── BOQ helpers ──────────────────────────────────────────────────────────────

const VALID_SIDES = new Set(["high side", "high_side", "highside", "high"]);

const normalizeSide = (raw = "") => {
  const v = raw.toString().toLowerCase().trim();
  if (VALID_SIDES.has(v)) return "high_side";
  return "low_side"; // default
};

// ── FULL SCOPE TREE (Task 4.5) ──────────────────────────────────────────────
// Groups a flat project_boq_items[] list into a nested tree keyed by each
// item's section_path — reproducing whatever depth the source BOQ actually
// had (1 level, 3 levels, or none), never forcing a fixed Part/Section
// shape. Items with an empty section_path (genuinely unstructured source)
// land in a synthetic "(Ungrouped)" bucket rather than being silently
// dropped — every saved item must be reachable from this tree.
const buildBoqTree = (items) => {
  const root = { children: {}, items: [] };
  items.forEach((item) => {
    const path = item.section_path?.length ? item.section_path : ["(Ungrouped)"];
    let node = root;
    path.forEach((segment) => {
      if (!node.children[segment]) node.children[segment] = { children: {}, items: [] };
      node = node.children[segment];
    });
    node.items.push(item);
  });
  return root;
};

// ── FULL SCOPE FLAT TABLE (trust-first default view) ────────────────────
// The original BOQ, as the client has always seen it: one continuous
// table, top to bottom, in real document order — not a collapsible tree.
// A user verifying "did the AI capture this correctly" compares against
// the physical/Excel document they already know, which is a flat table
// with bold section-header rows appearing inline as you scroll, never a
// click-to-expand accordion. Sorted by row_order (assigned at save time,
// reflecting the source file's real sequence); items missing row_order
// (older imports, pre-this-feature) keep whatever order they arrived in
// from the fetch — never crash, never reorder unpredictably.
const sortByRowOrder = (items) =>
  [...items].sort((a, b) => {
    const ao = a.row_order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.row_order ?? Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });

const sectionLabel = (item) =>
  item.section_path?.length ? item.section_path.join(" › ") : "(Ungrouped)";

// showRate: false for field roles — rupee figures are office-only (D-287).
const BoqFlatTable = ({ items, showRate = true }) => {
  const sorted = useMemo(() => sortByRowOrder(items), [items]);
  let lastSection = null;
  let serial = 0;

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-xs text-gray-500">
        <tr>
          <th className="p-2 text-left w-10">#</th>
          <th className="p-2 text-left">Item</th>
          <th className="p-2 text-center">Unit</th>
          <th className="p-2 text-right">Qty</th>
          {showRate && <th className="p-2 text-right">Rate</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {sorted.map((item) => {
          const section = sectionLabel(item);
          const showDivider = section !== lastSection;
          lastSection = section;
          serial += 1;
          return (
            <FragmentRow key={item.id} item={item} serial={serial}
              showDivider={showDivider} section={section} showRate={showRate} />
          );
        })}
      </tbody>
    </table>
  );
};

// Split into its own component (rather than inline) purely so each row
// can hold its own "expand full spec" toggle state independently.
const FragmentRow = ({ item, serial, showDivider, section, showRate = true }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      {showDivider && (
        <tr className="bg-gray-100">
          <td colSpan={showRate ? 5 : 4} className="px-2 py-1.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
            {section}
          </td>
        </tr>
      )}
      <tr>
        <td className="p-2 text-gray-400 align-top">{serial}</td>
        <td className="p-2 align-top">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-800">{item.item_name}</span>
            {item.brand_make && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-semibold">
                {item.brand_make}
              </span>
            )}
          </div>
          {item.description && (
            <button onClick={() => setExpanded((e) => !e)}
              className="text-xs text-green-600 font-semibold mt-1 block">
              {expanded ? "▾ Hide spec" : "▸ Full spec"}
            </button>
          )}
          {expanded && item.description && (
            <p className="text-xs text-gray-500 mt-1 bg-gray-50 rounded-lg p-2">{item.description}</p>
          )}
        </td>
        <td className="p-2 text-center text-gray-500 align-top">{item.unit}</td>
        <td className="p-2 text-right align-top">{item.qty}</td>
        {showRate && (
          <td className="p-2 text-right text-gray-600 align-top">
            {item.rate ? `₹${Number(item.rate).toLocaleString("en-IN")}` : "—"}
          </td>
        )}
      </tr>
    </>
  );
};

// One reference-item row — collapsed by default (only item_name/brand/
// qty/rate visible); the full description expands on demand so a large
// BOQ's tree stays scannable rather than showing every spec paragraph
// inline.
const BoqScopeItemRow = ({ item, showRate = true }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="py-2 border-b border-gray-50 last:border-b-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-800">{item.item_name}</span>
        {item.brand_make && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-semibold">
            {item.brand_make}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-0.5">
        {item.stage && <span className="mr-2">Stage: {item.stage}</span>}
        {item.qty} {item.unit}
        {showRate && item.rate ? ` × ₹${Number(item.rate).toLocaleString("en-IN")}` : ""}
      </p>
      {item.description && (
        <button onClick={() => setExpanded((e) => !e)}
          className="text-xs text-green-600 font-semibold mt-1">
          {expanded ? "▾ Hide spec" : "▸ Full spec"}
        </button>
      )}
      {expanded && item.description && (
        <p className="text-xs text-gray-500 mt-1 bg-gray-50 rounded-lg p-2">{item.description}</p>
      )}
    </div>
  );
};

// One section-tree node — recurses into child sections, then lists this
// node's own items. Top level (depth 0) starts expanded so the outline is
// visible on load; deeper levels start collapsed so a large BOQ doesn't
// dump hundreds of rows open at once.
const BoqTreeNode = ({ name, node, depth, showRate = true }) => {
  const [open, setOpen] = useState(depth === 0);
  const childKeys = Object.keys(node.children).sort();

  return (
    <div className={depth > 0 ? "ml-4 border-l border-gray-100 pl-3" : ""}>
      <button onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 py-1.5 text-sm font-semibold text-gray-700 hover:text-green-700">
        <span className="text-gray-400 text-xs">{open ? "▾" : "▸"}</span>
        {name}
        {node.items.length > 0 && (
          <span className="text-xs font-normal text-gray-400">({node.items.length})</span>
        )}
      </button>
      {open && (
        <div>
          {childKeys.map((key) => (
            <BoqTreeNode key={key} name={key} node={node.children[key]} depth={depth + 1} showRate={showRate} />
          ))}
          {node.items.length > 0 && (
            <div className="ml-4">
              {node.items.map((item) => <BoqScopeItemRow key={item.id} item={item} showRate={showRate} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const parseBOQFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb    = XLSX.read(e.target.result, { type: "binary" });
        const ws    = wb.Sheets[wb.SheetNames[0]];
        const rows  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

        if (!rows || rows.length < 2) {
          return reject(new Error("File is empty"));
        }

        // Validate headers: A=Stage Name, B=Side, C=Item Name, D=Qty, E=Unit, F=Rate
        const header = rows[0].map(h => String(h || "").toLowerCase().trim());
        const required = ["stage name", "side", "item name", "qty", "unit", "rate"];
        const hasHeaders = required.every(h => header.includes(h));
        if (!hasHeaders) {
          return reject(new Error("Invalid format. Please use ZENO template"));
        }

        const idxStage = header.indexOf("stage name");
        const idxSide  = header.indexOf("side");
        const idxItem  = header.indexOf("item name");
        const idxQty   = header.indexOf("qty");
        const idxUnit  = header.indexOf("unit");
        const idxRate  = header.indexOf("rate");

        const dataRows = rows.slice(1).filter(r =>
          r.some(cell => String(cell || "").trim() !== "")
        );
        if (dataRows.length === 0) return reject(new Error("File is empty"));

        const parsed = dataRows.map(r => ({
          stage_name:      String(r[idxStage] || "").trim(),
          side_raw:        String(r[idxSide]  || "").trim(),
          side_normalized: normalizeSide(r[idxSide]),
          item_name:       String(r[idxItem]  || "").trim(),
          qty:             Number(r[idxQty]   || 0),
          unit:            String(r[idxUnit]  || "Nos").trim(),
          rate:            Number(r[idxRate]  || 0),
        })).filter(r => r.stage_name && r.item_name);

        if (parsed.length === 0) return reject(new Error("File is empty"));

        resolve(parsed);
      } catch (err) {
        reject(new Error("Invalid format. Please use ZENO template"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsBinaryString(file);
  });

// embedded: true when rendered as a tab inside ProjectDetail.
export default function ProjectMaterials({ embedded = false }) {
  const { id: project_id } = useParams();
  const navigate           = useNavigate();
  const user               = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id         = user.company_id || "";
  const role               = user.role       || "";
  const canManage          = ["owner", "sm", "manager"].includes(role);
  // Rupee figures (budget/issued/remaining, BOQ rates, variation cost) are
  // office-only — same rule as ProjectDetail (D-287). Site Engineers work
  // with quantities, not money.
  const canSeeMoney        = canManage;

  const [project,  setProject]  = useState(null);
  const [stages,   setStages]   = useState([]);
  const [mrfs,     setMrfs]     = useState([]);
  const [boqItems, setBoqItems] = useState([]); // project_boq_items — Full Scope reference view (Task 4.5)
 const [scopeSearch, setScopeSearch] = useState("");
  // Default "table" — flat, source-order, matches the physical BOQ the
  // client already trusts. "tree" is opt-in for users who want section-
  // grouped navigation instead. See BoqFlatTable's comment for the why.
  const [scopeViewMode, setScopeViewMode] = useState("table");
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState("stages");

  // MUST stay above any early return (`if (loading) return ...` below) —
  // React requires the same hooks in the same order on every render;
  // placing these after that early return caused error #310 ("Rendered
  // fewer hooks than expected") since the loading-render would skip them
  // entirely while the loaded-render would call them.
  const filteredBoqItems = useMemo(() => {
    const q = scopeSearch.trim().toLowerCase();
    if (!q) return boqItems;
    return boqItems.filter((item) =>
      (item.item_name   || "").toLowerCase().includes(q) ||
      (item.description || "").toLowerCase().includes(q) ||
      (item.brand_make  || "").toLowerCase().includes(q)
    );
  }, [boqItems, scopeSearch]);

  const scopeTree = useMemo(() => buildBoqTree(filteredBoqItems), [filteredBoqItems]);

  // ── VARIATIONS ROLLUP (Priority 3 polish) ────────────────────────────────
  // Project-wide view aggregating every variation_log[] entry (Task B —
  // issue-time budget-exceed) and every revision_log[] entry with
  // triggers_variation: true (Task C — manual edit below already-issued
  // qty) across ALL stages. Pure read/derive from already-loaded `stages`
  // data — no new Firestore query, no new write-path. Owner/Manager no
  // longer has to open each stage's Edit Items modal individually to find
  // these.
  const allVariations = useMemo(() => {
    const rows = [];
    stages.forEach(stage => {
      (stage.material_budget || []).forEach(item => {
        (item.variation_log || []).forEach(v => {
          rows.push({
            source:        "issue_time",
            stage_name:    stage.stage_name,
            item_name:     item.item_name,
            unit:          item.unit,
            exceeded_qty:  v.exceeded_qty,
            extra_cost:    v.extra_cost,
            reason:        v.approval_note || "",
            approved_by:   v.approved_by_name || "",
            channel:       v.approval_channel || "",
            recorded_by:   v.recorded_by_name || "",
            at:            v.recorded_at || "",
          });
        });
        (item.revision_log || []).forEach(r => {
          if (!r.triggers_variation) return;
          rows.push({
            source:        "manual_edit",
            stage_name:    stage.stage_name,
            item_name:     item.item_name,
            unit:          item.unit,
            old_qty:       r.old_qty,
            new_qty:       r.new_qty,
            reason:        r.reason || "",
            recorded_by:   r.revised_by_name || "",
            at:            r.revised_at || "",
          });
        });
      });
    });
    // Most recent first
    return rows.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [stages]);

  const [showStageModal, setShowStageModal] = useState(false);
  const [stageForm, setStageForm] = useState({
    stage_name:       "",
    trigger_item_ids: [], // checklist item Firestore IDs, picked from the list below
    material_budget:  [{ item_id: "", item_code: "", item_name: "", unit: "Nos", qty_budgeted: "", cost_rate: "", side: "high_side", buffer_pct: 15 }],
  });
  const [triggerSearch, setTriggerSearch] = useState("");
  const [stageSaving, setStageSaving] = useState(false);
  const [deletingStageId, setDeletingStageId] = useState(null);

  const handleDeleteStage = async (stage) => {
    const confirmed = window.confirm(
      `Delete stage "${stage.stage_name}"? This can only be undone by recreating it manually. Only allowed if no material has been issued yet.`
    );
    if (!confirmed) return;
    try {
      setDeletingStageId(stage.id);
      await deleteMaterialStage(stage.id);
      await load();
    } catch (err) {
      alert(err.message || "Delete failed");
    } finally {
      setDeletingStageId(null);
    }
  };

  // ── Edit Stage Triggers (checklist-unlock linking, post-creation) ────────
  // Stages created via AI Import or BOQ Upload always start with
  // trigger_item_ids: [] (manual linking by design — D-149/D-177). This is
  // the missing "edit" half of that manual step — reuses the existing
  // governed updateMaterialStage() function, no new write-path.
  const [editTriggersStage, setEditTriggersStage] = useState(null);
  const [editTriggerIds,    setEditTriggerIds]    = useState([]);
  const [editTriggerSearch, setEditTriggerSearch] = useState("");
  const [savingTriggers,    setSavingTriggers]    = useState(false);

  // ── Edit Stage Items (Task C — qty/rate edit, add item, remove item) ────
  const [editItemsStage,     setEditItemsStage]     = useState(null);
  const [editItemsRows,      setEditItemsRows]      = useState([]);
  const [editItemsSaving,    setEditItemsSaving]    = useState(false);
  const [editItemSearch,     setEditItemSearch]     = useState({}); // { rowKey: "search text" }
  const [editItemPickerOpen, setEditItemPickerOpen] = useState(null); // rowKey showing picker

  const openEditItems = async (stage) => {
    setEditItemsStage(stage);
    setEditItemsRows(
      (stage.material_budget || []).map((item, idx) => ({
        _rowKey:      `existing-${idx}`,
        _isNew:       false,
        _removed:     false,
        _edit_reason: "",
        item_id:      item.item_id,
        item_code:    item.item_code || "",
        item_name:    item.item_name,
        unit:         item.unit,
        side:         item.side || "high_side",
        buffer_pct:   item.buffer_pct ?? 0,
        qty_budgeted: item.qty_budgeted,
        cost_rate:    item.cost_rate,
        qty_issued:   item.qty_issued || 0, // read-only display — never edited here
      }))
    );
    setEditItemSearch({});
    setEditItemPickerOpen(null);
    if (!itemMasterList.length) setItemMasterList(await fetchActiveItems(company_id));
  };

  const addEditItemRow = () => {
    setEditItemsRows(rows => [
      ...rows,
      {
        _rowKey: `new-${Date.now()}`, _isNew: true, _removed: false, _edit_reason: "",
        item_id: "", item_code: "", item_name: "", unit: "Nos", side: "high_side",
        buffer_pct: undefined, qty_budgeted: "", cost_rate: "", qty_issued: 0,
      },
    ]);
  };

  const selectEditItemMaster = (rowKey, masterItem) => {
    setEditItemsRows(rows => rows.map(r => r._rowKey === rowKey ? {
      ...r,
      item_id:   masterItem.item_id   || "",
      item_code: masterItem.item_code || "",
      item_name: masterItem.item_name || "",
      unit:      masterItem.unit      || r.unit || "Nos",
    } : r));
    setEditItemPickerOpen(null);
  };

  const updateEditItemRow = (rowKey, field, value) => {
    setEditItemsRows(rows => rows.map(r => r._rowKey === rowKey ? { ...r, [field]: value } : r));
  };

  const toggleRemoveEditItemRow = (rowKey) => {
    setEditItemsRows(rows => rows.map(r => r._rowKey === rowKey ? { ...r, _removed: !r._removed } : r));
  };

  const handleSaveEditItems = async () => {
    if (!editItemsStage) return;
    const finalItems = editItemsRows
      .filter(r => !r._removed)
      .map(r => ({
        item_id:      r.item_id,
        item_code:    r.item_code,
        item_name:    r.item_name,
        unit:         r.unit,
        side:         r.side,
        buffer_pct:   r._isNew ? undefined : r.buffer_pct,
        qty_budgeted: Number(r.qty_budgeted || 0),
        cost_rate:    Number(r.cost_rate || 0),
        _edit_reason: r._edit_reason,
      }));

    const unresolvedNew = finalItems.find(i => !i.item_id);
    if (unresolvedNew) {
      alert(`Please select "${unresolvedNew.item_name || "the new row"}" from the Item Master list before saving.`);
      return;
    }

    try {
      setEditItemsSaving(true);
      await updateStageItems(editItemsStage.id, finalItems, user);
      setEditItemsStage(null);
      await load();
    } catch (err) {
      alert(err.message || "Failed to save changes");
    } finally {
      setEditItemsSaving(false);
    }
  };

  const openEditTriggers = (stage) => {
    setEditTriggersStage(stage);
    setEditTriggerIds(stage.trigger_item_ids || []);
    setEditTriggerSearch("");
  };

  const toggleEditTriggerItem = (itemId) => {
    setEditTriggerIds(ids =>
      ids.includes(itemId) ? ids.filter(id => id !== itemId) : [...ids, itemId]
    );
  };

  const handleSaveTriggers = async () => {
    if (!editTriggersStage) return;
    try {
      setSavingTriggers(true);
      await updateMaterialStage(editTriggersStage.id, {
        trigger_item_ids: editTriggerIds,
      });
      setEditTriggersStage(null);
      await load();
    } catch (err) {
      alert("Failed to save: " + err.message);
    } finally {
      setSavingTriggers(false);
    }
  };
  // Item Master list + per-row search, so manual "New Stage" budget rows
  // capture a real item_id — without this, StoreMRFDetail.js can never
  // match issued items back to this stage's material_budget (item_id was
  // always blank), so qty_issued/qty_remaining never sync for manually
  // created stages even though real inventory deduction works fine.
  const [itemMasterList, setItemMasterList] = useState([]);
  const [budgetItemSearch, setBudgetItemSearch] = useState({}); // { rowIndex: "search text" }
  const [budgetPickerOpen, setBudgetPickerOpen] = useState(null); // rowIndex currently showing dropdown

  const [showAIBoqPanel, setShowAIBoqPanel] = useState(false);
  const [showMRFModal, setShowMRFModal] = useState(false);
  const [mrfStage,     setMrfStage]     = useState(null);
  const [mrfForm, setMrfForm] = useState({
    items:           [{ item_name: "", qty_requested: "", unit: "Nos", remarks: "" }],
    override_reason: "",
  });
  const [mrfSaving, setMrfSaving] = useState(false);

  // ── BULK INITIAL DISPATCH (Task A, item-level redesign) ─────────────────
  // Reuses the exact same material_requests write shape as handleCreateMRF
  // below — no new write-path. Loops per-stage (one MRF per stage) because
  // the MRF schema (stage_id/stage_no/stage_name) only ever supports a
  // single stage per doc; "one bulk MRF across all stages" would break
  // that schema and StoreMRFDetail.js's stage-matching logic.
  // Item-level selection (not a High/Low toggle) — practically, Managers
  // send material in planned batches, not everything-at-once; this lets
  // them pick exactly what goes in THIS batch, with sensible defaults
  // (High Side pre-checked, Low Side unchecked) rather than an all-or-
  // nothing switch.
  const [showBulkDispatchModal, setShowBulkDispatchModal] = useState(false);
  const [bulkSelectedItems, setBulkSelectedItems]         = useState({}); // { "stageId_itemIdx": true/false }
  // Editable dispatch qty per selected item — real-world Managers rarely
  // send an item's full remaining quantity in one batch (site storage
  // space, planned phased delivery). Defaults to the full remaining qty
  // (backward-compatible with the old always-full-qty behavior) but is
  // now editable per row, same pattern as the existing + MRF modal's
  // qty_requested input (max clamped to qty_remaining). Unchecking an
  // item does NOT clear its typed qty — a Manager who accidentally
  // toggles a checkbox shouldn't lose what they already typed.
  const [bulkQtyOverrides, setBulkQtyOverrides]           = useState({}); // { "stageId_itemIdx": number }
  // Transient flag per item — true for a moment right after a clamp
  // fires (typed value exceeded max), purely to flash the input's border
  // so the Manager notices the cap without a disruptive popup alert.
  const [bulkClampedKeys, setBulkClampedKeys]             = useState({});
  const [bulkOverrideReason, setBulkOverrideReason]       = useState("");
  const [bulkDispatchSaving, setBulkDispatchSaving]       = useState(false);

  // Flattened, per-stage list of eligible (remaining > 0) items, each
  // tagged with whether it's already sitting in a pending MRF for this
  // same stage — shown as a warning, never a hard block, so a Manager
  // can still deliberately re-request if that's genuinely intended.
  const bulkDispatchStages = useMemo(() => {
    return stages
      .map(stage => {
        const items = (stage.material_budget || [])
          .map((item, idx) => ({ ...item, _idx: idx }))
          .filter(item => Number(item.qty_remaining ?? item.qty_budgeted) > 0)
          .map(item => {
            const _key = `${stage.id}_${item._idx}`;
            // Sum of this item's qty across every OTHER pending MRF for this
            // stage — purely informational (never subtracted from the max
            // input's ceiling, which would turn a UI display into a second,
            // un-auditable source of truth for "true remaining"). The actual
            // stock-safety check stays exclusively at issueAgainstMR() time,
            // same as every other MRF creation path in this app — this just
            // gives the Manager visibility before they decide.
            const pendingMrfsForItem = mrfs.filter(m =>
              m.stage_id === stage.id &&
              m.status === "pending" &&
              (m.items || []).some(mi => item.item_id && mi.item_id === item.item_id)
            );
            const _pending = pendingMrfsForItem.length > 0;
            const _pendingQty = pendingMrfsForItem.reduce((sum, m) => {
              const mi = (m.items || []).find(x => x.item_id === item.item_id);
              return sum + Number(mi?.qty || 0);
            }, 0);
            return { ...item, _key, _pending, _pendingQty };
          });
        return { stage, items };
      })
      .filter(s => s.items.length > 0);
  }, [stages, mrfs]);

  const openBulkDispatch = () => {
    const defaults = {};
    const qtyDefaults = {};
    bulkDispatchStages.forEach(({ items }) => {
      items.forEach(item => {
        // Pending items default OFF (avoid accidental duplicate request);
        // otherwise High Side ON, Low Side OFF — same reasoning as before:
        // Low Side quantities are estimates until site measurement.
        defaults[item._key] = item.side !== "low_side" && !item._pending;
        // Default qty = full remaining (unchanged behavior unless the
        // Manager edits it) — never auto-decided smaller, since we have
        // no signal for what a "sensible partial" would be.
        qtyDefaults[item._key] = Number(item.qty_remaining ?? item.qty_budgeted) || 0;
      });
    });
    setBulkSelectedItems(defaults);
    setBulkQtyOverrides(qtyDefaults);
    setBulkClampedKeys({});
    setBulkOverrideReason("");
    setShowBulkDispatchModal(true);
  };

  const toggleBulkItem = (key) => {
    setBulkSelectedItems(s => ({ ...s, [key]: !s[key] }));
  };

  // Clamps to [0, maxQty]. If the typed value exceeded maxQty, briefly
  // flags this key so the input border flashes orange/red — a visible-
  // but-non-disruptive signal (no popup) that the value got capped,
  // since a bulk list of many rows would get noisy with a text warning
  // per row.
  const updateBulkQty = (key, value, maxQty) => {
    if (value === "") {
      setBulkQtyOverrides(q => ({ ...q, [key]: "" }));
      return;
    }
    const num = Number(value);
    if (isNaN(num)) return;
    const clamped = Math.min(Math.max(num, 0), maxQty);
    setBulkQtyOverrides(q => ({ ...q, [key]: clamped }));
    if (num > maxQty) {
      setBulkClampedKeys(c => ({ ...c, [key]: true }));
      setTimeout(() => {
        setBulkClampedKeys(c => {
          const { [key]: _removed, ...rest } = c;
          return rest;
        });
      }, 600);
    }
  };

  const toggleBulkStageAll = (items, value) => {
    setBulkSelectedItems(s => {
      const updated = { ...s };
      items.forEach(item => { updated[item._key] = value; });
      return updated;
    });
  };

  const bulkSelectAll = (value) => {
    setBulkSelectedItems(s => {
      const updated = { ...s };
      bulkDispatchStages.forEach(({ items }) => {
        items.forEach(item => { updated[item._key] = value; });
      });
      return updated;
    });
  };

  // Qty-aware — a checked item with a blanked-out or zero qty shouldn't
  // count toward "N selected" (matches the same rule handleBulkDispatch
  // uses to decide what actually gets dispatched, so this count is never
  // misleading right before submit).
  const bulkSelectedCount      = Object.entries(bulkSelectedItems)
    .filter(([key, checked]) => checked && Number(bulkQtyOverrides[key]) > 0).length;
  const bulkTouchesLockedStage = bulkDispatchStages.some(({ stage, items }) =>
    stage.status === "locked" && items.some(item => bulkSelectedItems[item._key])
  );

  
  
  // BOQ Upload states
  const [boqStageGroups,  setBoqStageGroups]  = useState([]);
  const [boqPreview,      setBoqPreview]      = useState(false);
  const [boqLoading,      setBoqLoading]      = useState(false);
  const [boqSaving,       setBoqSaving]       = useState(false);
  const [boqError,        setBoqError]        = useState("");
  const [replaceExisting, setReplaceExisting] = useState(new Set());
  const [existingStageMap,setExistingStageMap]= useState({});
  const [boqMasterItems,  setBoqMasterItems]  = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => { if (project_id) load(); }, [project_id]); // eslint-disable-line

  const load = async () => {
    try {
      setLoading(true);
      const [pSnap, stagesData, mrfsData, checklistData, boqItemsData] = await Promise.all([
        getDoc(doc(db, "projects", project_id)),
        fetchMaterialStages(company_id, project_id),
        fetchProjectMRFs(company_id, project_id),
        fetchChecklistItems(project_id, company_id),
        fetchBoqItems(company_id, project_id),
      ]);
      if (pSnap.exists()) {
        const pData = pSnap.data();
        // Company + assignment check (shared rule — projectAccess.js).
        // Also closes direct-URL access to /projects/:id/materials.
        if (!(await canAccessProject(pData, pSnap.id, user))) {
          alert("Access denied — you are not assigned to this project");
          navigate("/projects");
          return;
        }
        setProject({ id: pSnap.id, ...pData });
      }
      setStages(stagesData);
      setMrfs(mrfsData);
      setChecklistItems(checklistData);
      setBoqItems(boqItemsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addStageBudgetRow = () =>
    setStageForm(f => ({
      ...f,
      material_budget: [...f.material_budget,
        { item_id: "", item_code: "", item_name: "", unit: "Nos", qty_budgeted: "", cost_rate: "", side: "high_side", buffer_pct: 15 }],
    }));

  // Selecting an Item Master entry fills item_id/item_code/item_name/unit
  // together, and closes that row's picker.
  const selectBudgetItem = (rowIndex, masterItem) => {
    const updated = [...stageForm.material_budget];
    updated[rowIndex] = {
      ...updated[rowIndex],
      item_id:   masterItem.item_id   || "",
      item_code: masterItem.item_code || "",
      item_name: masterItem.item_name || "",
      unit:      masterItem.unit      || updated[rowIndex].unit || "Nos",
    };
    setStageForm(f => ({ ...f, material_budget: updated }));
    setBudgetPickerOpen(null);
  };

  const toggleTriggerItem = (itemId) => {
    setStageForm(f => ({
      ...f,
      trigger_item_ids: f.trigger_item_ids.includes(itemId)
        ? f.trigger_item_ids.filter(id => id !== itemId)
        : [...f.trigger_item_ids, itemId],
    }));
  };

  const updateStageBudgetRow = (i, field, val) => {
    const updated = [...stageForm.material_budget];
    updated[i][field] = val;
    setStageForm(f => ({ ...f, material_budget: updated }));
  };

  const removeStageBudgetRow = (i) =>
    setStageForm(f => ({
      ...f,
      material_budget: f.material_budget.filter((_, idx) => idx !== i),
    }));

  const handleCreateStage = async () => {
    if (!stageForm.stage_name.trim()) { alert("Stage name required"); return; }

    const filledRows = stageForm.material_budget.filter(i => i.item_name.trim());
    const unresolvedRow = filledRows.find(i => !i.item_id);
    if (unresolvedRow) {
      alert(`Please select "${unresolvedRow.item_name}" from the Item Master list (click the row and pick a match) — typing a name alone won't let Store track issued quantity against this stage.`);
      return;
    }

    try {
      setStageSaving(true);

      await createMaterialStage({
        project_id,
        project_name:      project?.project_name || "",
        stage_name:        stageForm.stage_name,
        trigger_item_ids:  stageForm.trigger_item_ids, // checklist item IDs — never positions
        material_budget:   filledRows,
      }, user);

      setShowStageModal(false);
      setStageForm({
        stage_name: "", trigger_item_ids: [],
        material_budget: [{ item_id: "", item_code: "", item_name: "", unit: "Nos", qty_budgeted: "", cost_rate: "", side: "high_side", buffer_pct: 15 }],
      });
      setTriggerSearch("");
      setBudgetItemSearch({});
      await load();
    } catch (err) {
      alert("Failed: " + err.message);
    } finally {
      setStageSaving(false);
    }
  };

  const openMRFModal = (stage) => {
    setMrfStage(stage);
    setMrfForm({
      items: (stage.material_budget || []).map(b => ({
        item_id:       b.item_id   || "",
        item_name:     b.item_name || "",
        unit:          b.unit      || "Nos",
        qty_remaining: b.qty_remaining ?? b.qty_budgeted,
        qty_requested: "",
        remarks:       "",
        side:          b.side      || "high_side",
      })),
      override_reason: "",
    });
    setShowMRFModal(true);
  };

  const updateMRFItem = (i, field, val) => {
    const updated = [...mrfForm.items];
    updated[i][field] = val;
    setMrfForm(f => ({ ...f, items: updated }));
  };

  const handleCreateMRF = async () => {
  const validItems = mrfForm.items.filter(i => i.item_name.trim() && i.qty_requested);
  if (!validItems.length) { alert("Please add at least one item with quantity."); return; }
  if (mrfStage?.status === "locked" && !mrfForm.override_reason.trim()) {
    alert("Stage is locked. Please provide an override reason."); return;
  }
  try {
    setMrfSaving(true);

    // Save to material_requests (store dashboard se connected)
    const { addDoc, collection, serverTimestamp } = await import("firebase/firestore");

    const finalItems = validItems.map(i => ({
      item_id:       i.item_id   || "",
      item_name:     i.item_name || "",
      unit:          i.unit      || "Nos",
      qty:           Number(i.qty_requested || 0),
      approved_qty:  Number(i.qty_requested || 0),
      issued_qty:    0,
      remaining_qty: Number(i.qty_requested || 0),
      remarks:       i.remarks   || "",
      side:          i.side      || "high_side",
      category:      "MATERIAL",
      is_custom_item: false,
      inventory_linked: !!i.item_id,
      item_status:   "pending",
    }));

    const mrfRef = await addDoc(collection(db, "material_requests"), {
      company_id:      company_id,
      source:          "project_stage",
      requested_by:    user.uid  || user.id || "",
      requested_by_name: user.name || "",
      project_id:      project_id,
      project_name:    project?.project_name || "",
      site_id:         project?.site_id      || "",
      site_name:       project?.site_name    || "",
      client_id:       project?.client_id    || "",
      client_name:     project?.client_name  || "",
      stage_id:        mrfStage?.id          || "",
      stage_no:        mrfStage?.stage_no    || 0,
      stage_name:      mrfStage?.stage_name  || "",
      stage_override:  mrfStage?.status === "locked",
      override_reason: mrfForm.override_reason || "",
      status:          "pending",
      is_processed:    false,
      created_at:      serverTimestamp(),
      items:           finalItems,
    });

    alert(`Material Request submitted successfully. Store will approve and issue.`);
    setShowMRFModal(false);
    await load();
  } catch (err) {
    alert("Failed: " + err.message);
  } finally {
    setMrfSaving(false);
  }
};

const handleBulkDispatch = async () => {
  const selectedByStage = {};
  bulkDispatchStages.forEach(({ stage, items }) => {
    // Only real, positive dispatch quantities count as "selected" — a
    // checked item with a blanked-out or zero qty input shouldn't create
    // a zero-qty MRF line.
    const selectedItems = items.filter(item =>
      bulkSelectedItems[item._key] && Number(bulkQtyOverrides[item._key]) > 0
    );
    if (selectedItems.length > 0) selectedByStage[stage.id] = { stage, items: selectedItems };
  });

  const stageIds = Object.keys(selectedByStage);
  if (stageIds.length === 0) {
    alert("Select at least one item to dispatch.");
    return;
  }

  const touchesLocked = stageIds.some(id => selectedByStage[id].stage.status === "locked");
  if (touchesLocked && !bulkOverrideReason.trim()) {
    alert("One or more selected items are in a Locked stage. Please provide an override reason to proceed.");
    return;
  }

  try {
    setBulkDispatchSaving(true);
    const { addDoc, collection, serverTimestamp } = await import("firebase/firestore");
    let mrfsCreated = 0;
    let itemsDispatched = 0;

    for (const stageId of stageIds) {
      const { stage, items } = selectedByStage[stageId];

      const finalItems = items.map(item => {
        const maxQty = Number(item.qty_remaining ?? item.qty_budgeted) || 0;
        // Re-clamp at submit time too — cheap insurance against the edge
        // case where the input's own onChange clamp got bypassed (e.g. a
        // paste/autofill event). The Manager-edited batch qty is always
        // what actually gets dispatched, never silently the full
        // remaining amount.
        const dispatchQty = Math.min(Number(bulkQtyOverrides[item._key]) || 0, maxQty);
        return {
          item_id:          item.item_id   || "",
          item_name:        item.item_name || "",
          unit:              item.unit      || "Nos",
          qty:               dispatchQty,
          approved_qty:      dispatchQty,
          issued_qty:        0,
          remaining_qty:     dispatchQty,
          remarks:           "Bulk initial dispatch",
          side:              item.side || "high_side",
          category:          "MATERIAL",
          is_custom_item:    false,
          inventory_linked:  !!item.item_id,
          item_status:       "pending",
        };
      });

      const isLocked = stage.status === "locked";

      // Same write shape/target as handleCreateMRF above — material_requests
      // is the only collection StoreMRFDetail.js's issue/approve flow ever
      // reads project-stage MRFs from (D-... material_requests hardcode).
      await addDoc(collection(db, "material_requests"), {
        company_id, source: "project_stage",
        requested_by:      user.uid || user.id || "",
        requested_by_name: user.name || "",
        project_id, project_name: project?.project_name || "",
        site_id:    project?.site_id    || "", site_name:   project?.site_name   || "",
        client_id:  project?.client_id  || "", client_name: project?.client_name || "",
        stage_id: stage.id, stage_no: stage.stage_no || 0, stage_name: stage.stage_name || "",
        stage_override:  isLocked,
        override_reason: isLocked ? bulkOverrideReason : "",
        status: "pending", is_processed: false,
        created_at: serverTimestamp(),
        items: finalItems,
      });
      mrfsCreated++;
      itemsDispatched += finalItems.length;
    }

    alert(
      `✅ Bulk dispatch complete!\n${itemsDispatched} item(s) across ${mrfsCreated} stage(s) raised as MRFs.\n` +
      `Store will approve and issue each.`
    );
    setShowBulkDispatchModal(false);
    await load();
  } catch (err) {
    alert("Bulk dispatch failed: " + err.message);
  } finally {
    setBulkDispatchSaving(false);
  }
};



  
  // ── BOQ DOWNLOAD TEMPLATE ──────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    try {
      const items = await fetchActiveItems(company_id);
      const boqData = [
        ["Stage Name", "Side", "Item Name", "Qty", "Unit", "Rate"],
        ["High Side Work", "High Side", "Copper Pipe 3/8", 100, "Mtr", 230],
        ["High Side Work", "High Side", "Copper Pipe 5/8", 50,  "Mtr", 320],
        ["Low Side Work",  "Low Side",  "Insulation 3/8",  100, "Mtr", 45],
        ["Low Side Work",  "Low Side",  "GI Sheet",        20,  "Nos", 300],
      ];
      const itemData = [
        ["Item Name", "Item Code", "Unit"],
        ...items.map(i => [i.item_name, i.item_code, i.unit]),
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(boqData), "BOQ Entry");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(itemData), "Item Master");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob  = new Blob([wbout], { type: "application/octet-stream" });
      const url   = URL.createObjectURL(blob);
      const a     = document.createElement("a");
      a.href      = url;
      a.download  = "ZENO_BOQ_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Template download failed: " + err.message);
    }
  };

  // ── BOQ UPLOAD ─────────────────────────────────────────────────────────────
  const handleBOQUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setBoqError("");
    setBoqLoading(true);
    try {
      const parsed      = await parseBOQFile(file);
      const items       = matchList(await fetchActiveItems(company_id), company_id);
      setBoqMasterItems(items);
      const existingMap = await fetchExistingStageNames(company_id, project_id);
      setExistingStageMap(existingMap);

      const groupMap = {};
      parsed.forEach(row => {
        const key = row.stage_name.toLowerCase().trim();
        if (!groupMap[key]) groupMap[key] = { stage_name: row.stage_name, rows: [] };
        // P4-3: exactly one item -> linked. 2+ items, or the same words in
        // another order -> the user picks or skips (never a guess, never a
        // silent duplicate). No match -> Map to existing or Create new.
        const match = resolveBoqRow(items, row);
        groupMap[key].rows.push({
          ...row, match, resolvedItem: match.item || null, matched: match.status === "linked",
          createNew: false, mapTo: "", skip: false,
        });
      });

      setBoqStageGroups(Object.values(groupMap));
      setBoqPreview(true);
    } catch (err) {
      setBoqError(err.message);
    } finally {
      setBoqLoading(false);
    }
  };

  // ── BOQ SAVE ───────────────────────────────────────────────────────────────
  const setBoqRow = (gi, ri, changes) => {
    setBoqStageGroups(groups => groups.map((g, gii) =>
      gii !== gi ? g : { ...g, rows: g.rows.map((r, rii) => (rii !== ri ? r : { ...r, ...changes })) }
    ));
  };

  const handleSaveBOQ = async () => {
    const { groups: finalGroups, skipped, unresolved } = decideBoqRows(boqStageGroups, boqMasterItems);

    if (unresolved.length > 0) {
      alert("Some items are unresolved. Please pick an item, skip the row, or select 'Create New'.");
      return;
    }
    if (finalGroups.length === 0) {
      alert("Every row is skipped. Nothing to import.");
      return;
    }
    const clashes = newNameClashes(
      finalGroups.flatMap(g => g.rows).filter(r => r.createNew && !r.resolvedItem?.item_id).map(r => r.item_name)
    );
    if (clashes.length > 0) {
      alert(`These new items look like the same item. Use one spelling in the file and upload again:\n${clashes.map(c => `• ${c.join(" / ")}`).join("\n")}`);
      return;
    }

    setBoqSaving(true);
    try {
      // D-300: one reserved platform-wide id per new item.
      for (const group of finalGroups) {
        for (const row of group.rows) {
          if (row.createNew && !row.resolvedItem?.item_id) {
            const item_id = await generateNextItemId();
            const created = await createNewItem(company_id, item_id, row.item_name, row.unit);
            row.resolvedItem = created;
          }
        }
      }

      const { stagesCreated, itemsImported } = await saveBOQStages({
        company_id,
        project_id,
        project_name:    project?.project_name || "",
        stages:          finalGroups,
        replaceExisting,
        existingStageMap,
        user,
      });

      const skippedNote = skipped.length > 0
        ? `\n\n⏭ Skipped rows, not imported (${skipped.length}):\n${skipped.map(s => `• ${s.item_name} (${s.stage_name}) — ${s.reason}`).join("\n")}`
        : "";
      alert(`✅ BOQ Imported!\n${stagesCreated} stages created\n${itemsImported} items imported${skippedNote}`);
      setBoqPreview(false);
      setBoqStageGroups([]);
      await load();
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setBoqSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  const checklistItemNameById = checklistItems.reduce((acc, i) => {
    acc[i.id] = i.item_name;
    return acc;
  }, {});

  return (
    <div className={embedded ? "space-y-5 pb-20" : "p-4 md:p-6 space-y-5 max-w-5xl mx-auto pb-20"}>

      {/* HEADER */}
      <div>
        {!embedded && (
          <button onClick={() => navigate(`/projects/${project_id}`)}
            className="text-gray-400 text-sm hover:text-gray-600 mb-1 block">
            ← {project?.project_name || "Project"}
          </button>
        )}
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Material Control</h1>
            <p className="text-sm text-gray-500 mt-0.5">{stages.length} stages · {mrfs.length} MRFs</p>
          </div>
          {canManage && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={handleDownloadTemplate}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200">
                📥 BOQ Template
              </button>
              <button onClick={() => setShowAIBoqPanel(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:opacity-90">
                🤖 AI Smart BOQ Import
              </button>
              <label className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 cursor-pointer">
                {boqLoading ? "Parsing..." : "📤 Upload BOQ (Template)"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleBOQUpload}
                />
              </label>
              <button onClick={async () => { setShowStageModal(true); if (!itemMasterList.length) setItemMasterList(await fetchActiveItems(company_id)); }}
                className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700">
                + New Stage
              </button>
              {stages.length > 0 && (
                <button onClick={openBulkDispatch}
                  className="px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-semibold hover:bg-teal-700">
                  📦 Bulk Dispatch
                </button>
              )}
            </div>
          )}
          {boqError && <p className="text-xs text-red-500 mt-1">{boqError}</p>}
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: "stages",     label: `📦 Stages (${stages.length})` },
          { key: "mrfs",       label: `📋 MRFs (${mrfs.length})` },
          { key: "scope",      label: `📖 Full Scope (${boqItems.length})` },
          { key: "variations", label: `⚠️ Variations (${allVariations.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              tab === t.key ? "border-green-600 text-green-700" : "border-transparent text-gray-500"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* STAGES TAB */}
      {tab === "stages" && (
        <div className="space-y-4">
          {stages.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">📦</div>
              <p className="font-semibold">No stages yet</p>
              {canManage && (
                <button onClick={async () => { setShowStageModal(true); if (!itemMasterList.length) setItemMasterList(await fetchActiveItems(company_id)); }}
                  className="mt-4 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold">
                  + Create First Stage
                </button>
              )}
            </div>
          ) : stages.map(stage => {
            const statusInfo  = STAGE_STATUS[stage.status] || STAGE_STATUS.locked;
            const totalBudget = (stage.material_budget || []).reduce((s, i) => s + Number(i.budget_amount || 0), 0);
            const totalIssued = (stage.material_budget || []).reduce((s, i) => s + Number(i.issued_amount || 0), 0);

            return (
              <div key={stage.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {stage.stage_no}
                    </span>
                    <div>
                      <span className="font-semibold text-gray-800">{stage.stage_name}</span>
                      {(stage.trigger_item_ids?.length > 0 || stage.trigger_items?.length > 0) && (
                        <span className="text-xs text-gray-400 ml-2">
                          unlocks on: {
                            stage.trigger_item_ids?.length > 0
                              ? stage.trigger_item_ids.map(tid => checklistItemNameById[tid] || "Unknown item").join(", ")
                              : `checklist item ${stage.trigger_items.join(", ")} (legacy)`
                          }
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <button onClick={() => openMRFModal(stage)}
                      disabled={stage.status === "locked" && !canManage}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-40">
                      + MRF
                    </button>
                    {canManage && (
                      <button onClick={() => openEditTriggers(stage)}
                        title="Set which checklist item unlocks this stage"
                        className="px-2 py-1 text-blue-400 hover:text-blue-600 text-xs font-semibold">
                        ✏️
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => openEditItems(stage)}
                        title="Edit item quantities/rates, add or remove items"
                        className="px-2 py-1 text-purple-400 hover:text-purple-600 text-xs font-semibold">
                        📝
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => handleDeleteStage(stage)}
                        disabled={deletingStageId === stage.id}
                        title="Only deletable if nothing has been issued from this stage yet"
                        className="px-2 py-1 text-red-400 hover:text-red-600 text-xs font-semibold disabled:opacity-40">
                        {deletingStageId === stage.id ? "..." : "🗑️"}
                      </button>
                    )}
                  </div>
                </div>

                {canSeeMoney && (
                  <div className="px-5 py-2 bg-gray-50 border-b flex gap-6 text-xs text-gray-500">
                    <span>Budget: <strong className="text-gray-700">₹{totalBudget.toLocaleString("en-IN")}</strong></span>
                    <span>Issued: <strong className="text-gray-700">₹{totalIssued.toLocaleString("en-IN")}</strong></span>
                    <span>Remaining: <strong className="text-gray-700">₹{(totalBudget - totalIssued).toLocaleString("en-IN")}</strong></span>
                  </div>
                )}

                {(stage.material_budget || []).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs">
                        <tr>
                          <th className="p-2 text-left">Item</th>
                          <th className="p-2 text-center">Unit</th>
                          <th className="p-2 text-right">Budgeted</th>
                          <th className="p-2 text-right">Issued</th>
                          <th className="p-2 text-right">Remaining</th>
                          <th className="p-2 text-center">Usage %</th>
                          <th className="p-2 text-center">Alert</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {stage.material_budget.map((item, i) => {
                          const alert = ALERT_LEVEL[item.alert_level || "ok"];
                          const pct   = item.budget_pct || 0;
                          return (
                            <tr key={i} className={
                              item.alert_level === "exhausted" ? "bg-red-50" :
                              item.alert_level === "warning"   ? "bg-amber-50" : ""
                            }>
                              <td className="p-2">
                                <div className="font-medium text-gray-800">{item.item_name}</div>
                                <div className="flex gap-1 mt-0.5">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                    item.side === "low_side"
                                      ? "bg-orange-100 text-orange-600"
                                      : "bg-blue-100 text-blue-600"
                                  }`}>
                                    {item.side === "low_side" ? "🔧 Low" : "🏗️ High"}
                                  </span>
                                  {item.side === "low_side" && item.buffer_pct > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-500">
                                      +{item.buffer_pct}% ({item.qty_entered}→{item.qty_budgeted})
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 text-center text-gray-500">{item.unit}</td>
                              <td className="p-2 text-right">{item.qty_budgeted}</td>
                              <td className="p-2 text-right">{item.qty_issued || 0}</td>
                              <td className="p-2 text-right">{item.qty_remaining ?? item.qty_budgeted}</td>
                              <td className="p-2 text-center">
                                <div className="flex items-center gap-1 justify-center">
                                  <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full ${
                                      pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-green-500"
                                    }`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                  </div>
                                  <span className="text-xs text-gray-500">{pct}%</span>
                                </div>
                              </td>
                              <td className="p-2 text-center">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${alert.bg} ${alert.color}`}>
                                  {alert.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MRFs TAB */}
      {tab === "mrfs" && (
        <div className="space-y-3">
          {mrfs.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">📋</div>
              <p className="font-semibold">No MRFs raised yet</p>
              <p className="text-sm mt-1">Use the + MRF button from the Stages tab.</p>
            </div>
          ) : mrfs.map(mrf => {
            const statusInfo = MRF_STATUS[mrf.status] || MRF_STATUS.pending;
            return (
              <div key={mrf.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-800">{mrf.mrf_no}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                      {mrf.stage_override && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">
                          Override
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Stage: {mrf.stage_name || "—"} · By: {mrf.requested_by_name}
                    </p>
                    {mrf.override_reason && (
                      <p className="text-xs text-red-500 mt-0.5">Override: {mrf.override_reason}</p>
                    )}
                  </div>
                  {canManage && mrf.type !== "return" &&
                   ["pending", "approved", "partially_issued"].includes(mrf.status) && (
                    <button onClick={() => navigate(`/store-mrf/${mrf.id}`)}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                      🏬 Process in Store →
                    </button>
                  )}
                  {canManage && mrf.status === "issued" && mrf.type !== "return" && (
                    <button onClick={() => navigate(`/inventory-return/${mrf.id}`)}
                      className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-200">
                      ↩️ Return Material →
                    </button>
                  )}
                  {mrf.type === "return" && (
                    <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-lg font-semibold">
                      ↩️ Return · Ref: {mrf.original_mrf_no}
                    </span>
                  )}
                </div>
                <div className="mt-3 border-t pt-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400">
                        <th className="text-left pb-1">Item</th>
                        <th className="text-center pb-1">Unit</th>
                        <th className="text-right pb-1">Qty</th>
                        <th className="text-left pb-1 pl-3">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(mrf.items || []).map((item, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="py-1 font-medium text-gray-700">{item.item_name}</td>
                          <td className="py-1 text-center text-gray-500">{item.unit}</td>
                          {/* material_requests items store "qty"; legacy project_mrfs used "qty_requested" */}
                          <td className="py-1 text-right font-semibold">{item.qty ?? item.qty_requested}</td>
                          <td className="py-1 pl-3 text-gray-400">{item.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FULL SCOPE TAB (Task 4.5) — read-only reference view of the
          original BOQ as specified (equipment included, full descriptions,
          brand/make, and the source's own section/part structure) —
          entirely separate from Stages' procurement/budget tracking. */}
      {tab === "scope" && (
        <div className="space-y-3">
          {boqItems.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">📖</div>
              <p className="font-semibold">No Full Scope reference yet</p>
              <p className="text-sm mt-1">Run "🤖 AI Smart BOQ Import" to populate this from the original BOQ file.</p>
            </div>
          ) : (
            <>
              <div className="flex gap-2 items-center flex-wrap">
                <input value={scopeSearch}
                  onChange={e => setScopeSearch(e.target.value)}
                  placeholder="Search item name, spec, or brand..."
                  className="border p-2 rounded-xl flex-1 min-w-[200px] text-sm outline-none focus:ring-2 focus:ring-green-300" />
                <div className="flex rounded-xl border overflow-hidden shrink-0">
                  <button onClick={() => setScopeViewMode("table")}
                    className={`px-3 py-2 text-xs font-semibold ${
                      scopeViewMode === "table" ? "bg-green-600 text-white" : "bg-white text-gray-500"
                    }`}>
                    📄 BOQ View
                  </button>
                  <button onClick={() => setScopeViewMode("tree")}
                    className={`px-3 py-2 text-xs font-semibold ${
                      scopeViewMode === "tree" ? "bg-green-600 text-white" : "bg-white text-gray-500"
                    }`}>
                    🌲 Tree View
                  </button>
                </div>
              </div>
              {filteredBoqItems.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No items match "{scopeSearch}".</p>
              ) : scopeViewMode === "table" ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                  <BoqFlatTable items={filteredBoqItems} showRate={canSeeMoney} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  {Object.keys(scopeTree.children).sort().map(key => (
                    <BoqTreeNode key={key} name={key} node={scopeTree.children[key]} depth={0} showRate={canSeeMoney} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* VARIATIONS TAB — project-wide rollup, read-only. Both sources
          (issue-time exceed via Task B, manual edit-below-issued via
          Task C) shown together, most recent first. */}
      {tab === "variations" && (
        <div className="space-y-3">
          {allVariations.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">✅</div>
              <p className="font-semibold">No variations recorded</p>
              <p className="text-sm mt-1">Budget exceeds and post-issue reductions will show up here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="p-2 text-left">Stage</th>
                    <th className="p-2 text-left">Item</th>
                    <th className="p-2 text-center">Type</th>
                    <th className="p-2 text-right">Change</th>
                    <th className="p-2 text-left">Reason</th>
                    <th className="p-2 text-left">By</th>
                    <th className="p-2 text-left">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {allVariations.map((v, i) => (
                    <tr key={i} className={v.source === "issue_time" ? "bg-red-50/40" : "bg-amber-50/40"}>
                      <td className="p-2 font-medium text-gray-800">{v.stage_name}</td>
                      <td className="p-2 text-gray-700">{v.item_name}</td>
                      <td className="p-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          v.source === "issue_time" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {v.source === "issue_time" ? "Budget Exceeded" : "Reduced Below Issued"}
                        </span>
                      </td>
                      <td className="p-2 text-right text-gray-700">
                        {v.source === "issue_time"
                          ? `+${v.exceeded_qty} ${v.unit}${canSeeMoney ? ` (₹${Number(v.extra_cost || 0).toLocaleString("en-IN")})` : ""}`
                          : `${v.old_qty} → ${v.new_qty} ${v.unit}`}
                      </td>
                      <td className="p-2 text-gray-500 max-w-xs truncate" title={v.reason}>{v.reason || "—"}</td>
                      <td className="p-2 text-gray-500">
                        {v.source === "issue_time"
                          ? `${v.approved_by || "—"}${v.channel ? ` (${v.channel})` : ""} · rec. ${v.recorded_by || "—"}`
                          : (v.recorded_by || "—")}
                      </td>
                      <td className="p-2 text-gray-400 whitespace-nowrap">
                        {v.at ? new Date(v.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL: CREATE STAGE */}
      {showStageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-lg font-bold text-gray-800">New Material Stage</h2>
              <button onClick={() => setShowStageModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Stage Name *</label>
                <input value={stageForm.stage_name}
                  onChange={e => setStageForm(f => ({ ...f, stage_name: e.target.value }))}
                  placeholder="e.g. Stage 1 — Equipment Delivery"
                  className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-green-300" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  Unlock on Checklist Item(s)
                </label>
                <input value={triggerSearch}
                  onChange={e => setTriggerSearch(e.target.value)}
                  placeholder="Search checklist items..."
                  className="border p-2 rounded-xl w-full text-sm outline-none mb-2" />
                <div className="border rounded-xl max-h-48 overflow-y-auto">
                  {checklistItems.length === 0 ? (
                    <p className="text-xs text-gray-400 p-3">No checklist items found for this project yet.</p>
                  ) : (
                    checklistItems
                      .filter(i => i.item_name.toLowerCase().includes(triggerSearch.toLowerCase()))
                      .map(i => (
                        <label key={i.id}
                          className="flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox"
                            checked={stageForm.trigger_item_ids.includes(i.id)}
                            onChange={() => toggleTriggerItem(i.id)} />
                          {i.item_no && <span className="text-xs text-gray-400 font-mono">#{i.item_no}</span>}
                          <span className="text-gray-700">{i.item_name}</span>
                        </label>
                      ))
                  )}
                </div>
                {stageForm.trigger_item_ids.length > 0 && (
                  <p className="text-xs text-green-600 mt-1 font-semibold">
                    {stageForm.trigger_item_ids.length} item(s) selected — this stage unlocks as soon as ANY ONE of them is marked complete.
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">Pick the exact checklist item(s) (e.g. "2nd Floor Piping Done") that should trigger this stage's unlock.</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-semibold text-gray-500">Material Budget</label>
                  <button onClick={addStageBudgetRow} className="text-xs text-green-600 font-semibold hover:underline">
                    + Add Item
                  </button>
                </div>

                <div className="flex gap-4 mb-3 text-xs text-gray-400 bg-gray-50 p-2 rounded-lg">
                  <span><span className="w-2 h-2 rounded-full bg-blue-400 inline-block mr-1"/>🏗️ High Side — ODU, Chiller, Pumps (0% buffer)</span>
                  <span><span className="w-2 h-2 rounded-full bg-orange-400 inline-block mr-1"/>🔧 Low Side — Pipe, Wire, Duct (15% buffer)</span>
                </div>

                <div className="space-y-2">
                  {stageForm.material_budget.map((item, i) => (
                    <div key={i} className={`rounded-xl border p-3 space-y-2 ${
                      item.side === "low_side" ? "border-orange-100 bg-orange-50/30" : "border-gray-100 bg-gray-50/30"
                    }`}>
                      {/* Row 1 — Side, Name, Unit, Remove */}
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-3">
                          <select value={item.side || "high_side"}
                            onChange={e => updateStageBudgetRow(i, "side", e.target.value)}
                            className={`border p-1.5 rounded-lg w-full text-xs outline-none font-semibold ${
                              item.side === "low_side"
                                ? "bg-orange-50 border-orange-200 text-orange-700"
                                : "bg-blue-50 border-blue-200 text-blue-700"
                            }`}>
                            <option value="high_side">🏗️ High Side</option>
                            <option value="low_side">🔧 Low Side</option>
                          </select>
                        </div>
                        <div className="col-span-5" style={{ position: "relative" }}>
                          {item.item_id ? (
                            <div className="flex items-center gap-1 border border-green-200 bg-green-50 rounded-lg px-2 py-1.5">
                              <span className="text-xs text-green-700 font-semibold flex-1 truncate">✓ {item.item_name}</span>
                              <button type="button"
                                onClick={() => {
                                  const updated = [...stageForm.material_budget];
                                  updated[i] = { ...updated[i], item_id: "", item_code: "", item_name: "" };
                                  setStageForm(f => ({ ...f, material_budget: updated }));
                                }}
                                className="text-green-600 hover:text-red-500 text-xs">✕</button>
                            </div>
                          ) : (
                            <input
                              value={budgetItemSearch[i] ?? item.item_name}
                              onChange={e => {
                                setBudgetItemSearch(s => ({ ...s, [i]: e.target.value }));
                                setBudgetPickerOpen(i);
                              }}
                              onFocus={() => setBudgetPickerOpen(i)}
                              placeholder="Search item master..."
                              className="border p-1.5 rounded-lg w-full text-xs outline-none" />
                          )}
                          {budgetPickerOpen === i && !item.item_id && (
                            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                              {itemMasterList
                                .filter(m => m.item_name.toLowerCase().includes((budgetItemSearch[i] || "").toLowerCase()))
                                .slice(0, 20)
                                .map(m => (
                                  <div key={m.item_id}
                                    onClick={() => selectBudgetItem(i, m)}
                                    className="px-2 py-1.5 text-xs hover:bg-green-50 cursor-pointer border-b last:border-b-0">
                                    {m.item_name} <span className="text-gray-400">({m.item_code})</span>
                                  </div>
                                ))}
                              {itemMasterList.filter(m => m.item_name.toLowerCase().includes((budgetItemSearch[i] || "").toLowerCase())).length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-gray-400">No match found in Item Master.</div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="col-span-2">
                          <select value={item.unit}
                            onChange={e => updateStageBudgetRow(i, "unit", e.target.value)}
                            className="border p-1.5 rounded-lg w-full text-xs outline-none bg-white">
                            {["Nos", "Mtr", "Kg", "Set", "Lot", "LS", "Rmt", "Sqft"].map(u =>
                              <option key={u} value={u}>{u}</option>
                            )}
                          </select>
                        </div>
                        <div className="col-span-2 flex justify-end">
                          {stageForm.material_budget.length > 1 && (
                            <button onClick={() => removeStageBudgetRow(i)}
                              className="text-gray-300 hover:text-red-500 text-xs">✕ Remove</button>
                          )}
                        </div>
                      </div>

                      {/* Row 2 — Qty, Buffer, Rate, Amount */}
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-3 text-xs text-gray-400 pl-1">
                          {item.side === "low_side" ? "Qty (drawing)" : "Qty (exact)"}
                        </div>
                        <div className="col-span-2">
                          <input type="number" min="0" value={item.qty_budgeted}
                            onChange={e => updateStageBudgetRow(i, "qty_budgeted", e.target.value)}
                            placeholder="0"
                            className="border p-1.5 rounded-lg w-full text-xs outline-none text-right" />
                        </div>

                        {item.side === "low_side" ? (
                          <>
                            <div className="col-span-2 flex items-center gap-1 text-xs text-orange-600 font-semibold justify-center">
                              +<input type="number" min="0" max="50"
                                value={item.buffer_pct ?? 15}
                                onChange={e => updateStageBudgetRow(i, "buffer_pct", Number(e.target.value))}
                                className="border border-orange-200 p-1 rounded-lg w-10 text-xs outline-none text-center bg-orange-50" />%
                            </div>
                            <div className="col-span-2 text-xs text-orange-700 font-semibold text-center">
                              = {Math.ceil(Number(item.qty_budgeted || 0) * (1 + (item.buffer_pct ?? 15) / 100))}
                              <span className="text-orange-400 ml-1">{item.unit}</span>
                            </div>
                          </>
                        ) : (
                          <div className="col-span-4 text-xs text-blue-600 font-semibold pl-2">
                            = {Number(item.qty_budgeted || 0)} {item.unit} (exact)
                          </div>
                        )}

                        <div className="col-span-2">
                          <input type="number" min="0" value={item.cost_rate}
                            onChange={e => updateStageBudgetRow(i, "cost_rate", e.target.value)}
                            placeholder="Rate ₹"
                            className="border p-1.5 rounded-lg w-full text-xs outline-none text-right" />
                        </div>
                        <div className="col-span-3 text-xs text-right text-gray-600 font-semibold">
                          ₹{(Math.ceil(
                            Number(item.qty_budgeted || 0) *
                            (item.side === "low_side" ? (1 + (item.buffer_pct ?? 15) / 100) : 1)
                          ) * Number(item.cost_rate || 0)).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setShowStageModal(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleCreateStage} disabled={stageSaving}
                className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                {stageSaving ? "Creating..." : "Create Stage ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CREATE MRF */}
      {showMRFModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">New MRF</h2>
                <p className="text-xs text-gray-500 mt-0.5">Stage: {mrfStage?.stage_name}</p>
              </div>
              <button onClick={() => setShowMRFModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {mrfStage?.status === "locked" && (
              <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-xs text-red-600 font-semibold">⚠️ Stage is locked. Override reason is required.</p>
                <input value={mrfForm.override_reason}
                  onChange={e => setMrfForm(f => ({ ...f, override_reason: e.target.value }))}
                  placeholder="Enter override reason..."
                  className="mt-2 border border-red-200 p-2 rounded-lg w-full text-xs outline-none" />
              </div>
            )}

            <div className="p-5 space-y-2">
              <p className="text-xs font-semibold text-gray-500 mb-2">Enter quantity for each item (remaining budget shown).</p>
              {mrfForm.items.map((item, i) => (
                <div key={i} className={`rounded-xl border p-3 ${
                  item.side === "low_side" ? "border-orange-100 bg-orange-50/20" : "border-gray-100"
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        item.side === "low_side" ? "bg-orange-100 text-orange-600" : "bg-blue-100 text-blue-600"
                      }`}>
                        {item.side === "low_side" ? "🔧 Low" : "🏗️ High"}
                      </span>
                      <span className="text-sm font-medium text-gray-800">{item.item_name}</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      Remaining: <strong className="text-gray-700">{item.qty_remaining} {item.unit}</strong>
                    </span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input type="number" min="0" max={item.qty_remaining}
                      value={item.qty_requested}
                      onChange={e => updateMRFItem(i, "qty_requested", e.target.value)}
                      placeholder={`Max ${item.qty_remaining}`}
                      className="border p-2 rounded-lg text-sm outline-none text-right w-28" />
                    <span className="text-xs text-gray-500">{item.unit}</span>
                    <input value={item.remarks || ""}
                      onChange={e => updateMRFItem(i, "remarks", e.target.value)}
                      placeholder="Remarks (optional)"
                      className="border p-2 rounded-lg text-xs outline-none flex-1" />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setShowMRFModal(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleCreateMRF} disabled={mrfSaving}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {mrfSaving ? "Submitting..." : "Submit MRF ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* MODAL: BULK INITIAL DISPATCH (Task A, item-level) */}
      {showBulkDispatchModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">📦 Bulk Initial Dispatch</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pick exactly what to send in this batch — raises one MRF per stage that has selected items. Run this again for the next batch.
                </p>
              </div>
              <button onClick={() => setShowBulkDispatchModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex gap-4">
                <button onClick={() => bulkSelectAll(true)} className="text-xs font-semibold text-green-600 hover:underline">Select All</button>
                <button onClick={() => bulkSelectAll(false)} className="text-xs font-semibold text-gray-500 hover:underline">Clear All</button>
              </div>

              {bulkDispatchStages.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No items with remaining quantity to dispatch.</p>
              ) : bulkDispatchStages.map(({ stage, items }) => {
                // Qty-aware, same rule as bulkSelectedCount above — a
                // checked-but-zero-qty item doesn't count as selected.
                const selectedCount = items.filter(item =>
                  bulkSelectedItems[item._key] && Number(bulkQtyOverrides[item._key]) > 0
                ).length;
                return (
                  <div key={stage.id} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">{stage.stage_name}</span>
                        {stage.status === "locked" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-semibold">Locked</span>
                        )}
                        <span className="text-xs text-gray-400">{selectedCount}/{items.length} selected</span>
                      </div>
                      <button onClick={() => toggleBulkStageAll(items, selectedCount < items.length)}
                        className="text-xs text-blue-600 font-semibold hover:underline">
                        {selectedCount < items.length ? "Select all in stage" : "Clear stage"}
                      </button>
                    </div>
                  
                    <div className="divide-y divide-gray-50">
                      {items.map(item => {
                        const maxQty = Number(item.qty_remaining ?? item.qty_budgeted) || 0;
                        const isClamped = !!bulkClampedKeys[item._key];
                        return (
                        <div key={item._key} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 flex-wrap">
                          <input type="checkbox"
                            checked={!!bulkSelectedItems[item._key]}
                            onChange={() => toggleBulkItem(item._key)} />
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${
                            item.side === "low_side" ? "bg-orange-100 text-orange-600" : "bg-blue-100 text-blue-600"
                          }`}>
                            {item.side === "low_side" ? "🔧 Low" : "🏗️ High"}
                          </span>
                          <span className="text-sm text-gray-800 flex-1 min-w-[180px]">{item.item_name}</span>
                           <div className="flex items-center gap-1 shrink-0">
                            <input type="number" min="0" max={maxQty} step="any"
                              value={bulkQtyOverrides[item._key] ?? maxQty}
                              disabled={!bulkSelectedItems[item._key]}
                              onChange={e => updateBulkQty(item._key, e.target.value, maxQty)}
                              onClick={e => e.stopPropagation()}
                              className={`border p-1 rounded-lg w-20 text-xs outline-none text-right disabled:bg-gray-100 disabled:text-gray-400 transition-colors ${
                                isClamped ? "border-red-400 ring-1 ring-red-300" : ""
                              }`} />
                            <span className="text-xs text-gray-400">/ {maxQty} {item.unit}</span>
                          </div>
                          {item._pending && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold shrink-0"
                              title="Informational only — this qty is NOT subtracted from the max above. The real stock check happens when Store issues each MRF.">
                              ⏳ {item._pendingQty > 0 ? `${item._pendingQty} ${item.unit} ` : ""}already pending elsewhere
                            </span>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {bulkTouchesLockedStage && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">
                    Override reason (required — selection includes a Locked stage)
                  </label>
                  <input value={bulkOverrideReason}
                    onChange={e => setBulkOverrideReason(e.target.value)}
                    placeholder="e.g. Project kickoff — bulk material dispatch"
                    className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-teal-300" />
                  <p className="text-xs text-gray-400 mt-1">Applied to every Locked stage's MRF in this batch.</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setShowBulkDispatchModal(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleBulkDispatch} disabled={bulkDispatchSaving || bulkSelectedCount === 0}
                className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50">
                {bulkDispatchSaving ? "Dispatching..." : `Dispatch Selected (${bulkSelectedCount}) →`}
              </button>
            </div>
          </div>
        </div>
      )}


    {/* MODAL: EDIT STAGE TRIGGERS */}
      {editTriggersStage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Edit Unlock Trigger</h2>
                <p className="text-xs text-gray-500 mt-0.5">Stage: {editTriggersStage.stage_name}</p>
              </div>
              <button onClick={() => setEditTriggersStage(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs font-semibold text-gray-500 block mb-1">
                Unlock on Checklist Item(s)
              </label>
              <input value={editTriggerSearch}
                onChange={e => setEditTriggerSearch(e.target.value)}
                placeholder="Search checklist items..."
                className="border p-2 rounded-xl w-full text-sm outline-none mb-2" />
              <div className="border rounded-xl max-h-64 overflow-y-auto">
                {checklistItems.length === 0 ? (
                  <p className="text-xs text-gray-400 p-3">No checklist items found for this project yet — add checklist items first.</p>
                ) : (
                  checklistItems
                    .filter(i => i.item_name.toLowerCase().includes(editTriggerSearch.toLowerCase()))
                    .map(i => (
                      <label key={i.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox"
                          checked={editTriggerIds.includes(i.id)}
                          onChange={() => toggleEditTriggerItem(i.id)} />
                        {i.item_no && <span className="text-xs text-gray-400 font-mono">#{i.item_no}</span>}
                        <span className="text-gray-700">{i.item_name}</span>
                      </label>
                    ))
                )}
              </div>
              {editTriggerIds.length > 0 ? (
                <p className="text-xs text-green-600 font-semibold">
                  {editTriggerIds.length} item(s) selected — this stage unlocks as soon as ANY ONE is marked complete.
                </p>
              ) : (
                <p className="text-xs text-amber-600 font-semibold">
                  ⚠ No trigger selected — this stage will stay Locked until you pick at least one checklist item, or a Manager can still raise an MRF against it with an override reason.
                </p>
              )}
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setEditTriggersStage(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleSaveTriggers} disabled={savingTriggers}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {savingTriggers ? "Saving..." : "Save Trigger ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* MODAL: EDIT STAGE ITEMS (Task C) */}
      {editItemsStage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">📝 Edit Items</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Stage: {editItemsStage.stage_name} — editing qty/rate never changes what's already
                  issued, only what future issues will use.
                </p>
              </div>
              <button onClick={() => setEditItemsStage(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="p-5 space-y-2">
              {editItemsRows.map((row) => {
                const maxSafeQty = Number(row.qty_issued || 0);
                const willReduceBelowIssued = !row._isNew &&
                  Number(row.qty_budgeted || 0) < maxSafeQty;
                return (
                  <div key={row._rowKey} className={`rounded-xl border p-3 space-y-2 ${
                    row._removed ? "opacity-40 border-red-200 bg-red-50/30" : "border-gray-100"
                  }`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4" style={{ position: "relative" }}>
                        {row.item_id ? (
                          <span className="text-sm font-medium text-gray-800">{row.item_name}</span>
                        ) : (
                          <>
                            <input
                              value={editItemSearch[row._rowKey] ?? ""}
                              onChange={e => {
                                setEditItemSearch(s => ({ ...s, [row._rowKey]: e.target.value }));
                                setEditItemPickerOpen(row._rowKey);
                              }}
                              onFocus={() => setEditItemPickerOpen(row._rowKey)}
                              placeholder="Search item master..."
                              className="border p-1.5 rounded-lg w-full text-xs outline-none" />
                            {editItemPickerOpen === row._rowKey && (
                              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                {itemMasterList
                                  .filter(m => m.item_name.toLowerCase().includes((editItemSearch[row._rowKey] || "").toLowerCase()))
                                  .slice(0, 20)
                                  .map(m => (
                                    <div key={m.item_id}
                                      onClick={() => selectEditItemMaster(row._rowKey, m)}
                                      className="px-2 py-1.5 text-xs hover:bg-green-50 cursor-pointer border-b last:border-b-0">
                                      {m.item_name} <span className="text-gray-400">({m.item_code})</span>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="col-span-2 text-xs">
                        {row._isNew ? (
                          <select value={row.side}
                            onChange={e => updateEditItemRow(row._rowKey, "side", e.target.value)}
                            className="border p-1 rounded-lg w-full text-xs outline-none bg-white">
                            <option value="high_side">🏗️ High</option>
                            <option value="low_side">🔧 Low</option>
                          </select>
                        ) : (
                          <span className="text-gray-400 block">
                            {row.unit}
                            <span className="block text-gray-300">Issued: {row.qty_issued}</span>
                          </span>
                        )}
                      </div>
                      <div className="col-span-2">
                        <input type="number" min="0" value={row.qty_budgeted}
                          onChange={e => updateEditItemRow(row._rowKey, "qty_budgeted", e.target.value)}
                          disabled={row._removed}
                          placeholder="Qty"
                          className="border p-1.5 rounded-lg w-full text-xs outline-none text-right disabled:bg-gray-100" />
                      </div>
                      <div className="col-span-2">
                        <input type="number" min="0" value={row.cost_rate}
                          onChange={e => updateEditItemRow(row._rowKey, "cost_rate", e.target.value)}
                          disabled={row._removed}
                          placeholder="Rate ₹"
                          className="border p-1.5 rounded-lg w-full text-xs outline-none text-right disabled:bg-gray-100" />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        {row._isNew ? (
                          <button onClick={() => setEditItemsRows(rows => rows.filter(r => r._rowKey !== row._rowKey))}
                            className="text-gray-300 hover:text-red-500 text-xs">✕ Discard</button>
                        ) : (
                          <button onClick={() => toggleRemoveEditItemRow(row._rowKey)}
                            title={maxSafeQty > 0 ? "Cannot remove — material already issued against this item" : "Remove this item"}
                            disabled={maxSafeQty > 0}
                            className="text-gray-300 hover:text-red-500 text-xs disabled:opacity-30 disabled:cursor-not-allowed">
                            {row._removed ? "↺ Undo" : "🗑️ Remove"}
                          </button>
                        )}
                      </div>
                    </div>
                    {willReduceBelowIssued && !row._removed && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                        <p className="text-xs text-amber-700 font-semibold">
                          ⚠️ New quantity is below what's already issued ({maxSafeQty} {row.unit}). This will be
                          logged as a variation — please provide a reason.
                        </p>
                        <input value={row._edit_reason}
                          onChange={e => updateEditItemRow(row._rowKey, "_edit_reason", e.target.value)}
                          placeholder="Reason for reducing below issued quantity..."
                          className="mt-1.5 border border-amber-200 p-1.5 rounded-lg w-full text-xs outline-none" />
                      </div>
                    )}
                  </div>
                );
              })}
              <button onClick={addEditItemRow} className="text-xs text-green-600 font-semibold hover:underline">
                + Add Item
              </button>
            </div>

            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setEditItemsStage(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleSaveEditItems} disabled={editItemsSaving}
                className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
                {editItemsSaving ? "Saving..." : "Save Changes ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* MODAL: AI BOQ IMPORT */}
      {showAIBoqPanel && (
        <AIBoqImportPanel
          project_id={project_id}
          project_name={project?.project_name || ""}
          user={user}
          checklistItems={checklistItems}
          onClose={() => setShowAIBoqPanel(false)}
          onDone={async () => { setShowAIBoqPanel(false); await load(); }}
        />
      )}

    {/* MODAL: BOQ PREVIEW */}
      {boqPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">

            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">📋 BOQ Preview</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {boqStageGroups.length} stages · {boqStageGroups.reduce((s,g)=>s+g.rows.length,0)} items
                </p>
              </div>
              <button onClick={() => setBoqPreview(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="p-5 space-y-4">
              {boqStageGroups.map((group, gi) => {
                const nameKey       = group.stage_name.toLowerCase().trim();
                const alreadyExists = !!existingStageMap[nameKey];
                const unmatchedCount = group.rows.filter(r => !r.matched).length;
                return (
                  <div key={gi} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800">{group.stage_name}</span>
                        {alreadyExists && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Already exists</span>
                        )}
                        {unmatchedCount > 0 && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">⚠️ {unmatchedCount} unmatched</span>
                        )}
                      </div>
                      {alreadyExists && (
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input type="checkbox"
                            checked={replaceExisting.has(nameKey)}
                            onChange={e => {
                              const s = new Set(replaceExisting);
                              e.target.checked ? s.add(nameKey) : s.delete(nameKey);
                              setReplaceExisting(s);
                            }}
                          />
                          Replace existing
                        </label>
                      )}
                    </div>

                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500">
                        <tr>
                          <th className="p-2 text-left">Item</th>
                          <th className="p-2 text-center">Side</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-center">Unit</th>
                          <th className="p-2 text-right">Rate</th>
                          <th className="p-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {group.rows.map((row, ri) => (
                          <tr key={ri} className={!row.matched ? "bg-red-50" : ""}>
                            <td className="p-2">
                              <div className="font-medium text-gray-800">{row.item_name}</div>
                              {/* P4-3: 2+ items fit -> pick one or skip; no "Create new"
                                  (createItem would refuse it, or make a duplicate). */}
                              {row.match?.status === "ambiguous" && (
                                <div className="mt-1 space-y-1">
                                  <div className="text-xs font-medium text-amber-700">{matchReasonText(row.match)}</div>
                                  <select
                                    className="text-xs border rounded px-1 py-0.5 w-full"
                                    value={row.mapTo || ""}
                                    disabled={row.skip}
                                    onChange={e => setBoqRow(gi, ri, { mapTo: e.target.value })}
                                  >
                                    <option value="">— Pick one item —</option>
                                    {row.match.candidates.map(item => (
                                      <option key={item.id} value={item.item_id || ""} disabled={!!item._blocked}>
                                        {candidateLabel(row.match, item)}
                                      </option>
                                    ))}
                                  </select>
                                  <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                                    <input type="checkbox"
                                      checked={row.skip || false}
                                      onChange={e => setBoqRow(gi, ri, { skip: e.target.checked, mapTo: e.target.checked ? "" : row.mapTo })}
                                    />
                                    Skip this row
                                  </label>
                                </div>
                              )}
                              {row.match?.status === "not_found" && (
                                <div className="mt-1 space-y-1">
                                  <select
                                    className="text-xs border rounded px-1 py-0.5 w-full"
                                    value={row.mapTo || ""}
                                    onChange={e => setBoqRow(gi, ri, { mapTo: e.target.value, createNew: false })}
                                  >
                                    <option value="">— Map to existing item —</option>
                                    {boqMasterItems.filter(item => !item._blocked).map(item => (
                                      <option key={item.id} value={item.item_id}>
                                        {itemOptionLabel(item)}
                                      </option>
                                    ))}
                                  </select>
                                  <label className="flex items-center gap-1 text-xs text-blue-600 cursor-pointer">
                                    <input type="checkbox"
                                      checked={row.createNew || false}
                                      onChange={e => setBoqRow(gi, ri, { createNew: e.target.checked, mapTo: e.target.checked ? "" : row.mapTo })}
                                    />
                                    Create as new item in Item Master
                                  </label>
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                                row.side_normalized === "high_side"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}>
                                {row.side_normalized === "high_side" ? "🏗️ High" : "🔧 Low"}
                              </span>
                            </td>
                            <td className="p-2 text-right font-semibold">{row.qty}</td>
                            <td className="p-2 text-center text-gray-500">{row.unit}</td>
                            <td className="p-2 text-right text-gray-600">{row.rate ? `₹${row.rate}` : "—"}</td>
                            <td className="p-2 text-center">
                              {row.matched   ? <span className="text-xs text-green-600 font-semibold">✅ Matched</span>
                              : row.skip      ? <span className="text-xs text-gray-500 font-semibold">⏭ Skipped</span>
                              : row.createNew ? <span className="text-xs text-blue-600 font-semibold">🆕 New</span>
                              : row.mapTo     ? <span className="text-xs text-amber-600 font-semibold">🔗 Mapped</span>
                              :                 <span className="text-xs text-red-500 font-semibold">⚠️ Resolve</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            <div className="p-5 border-t bg-gray-50">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{boqStageGroups.length} stages</span>
                  {" · "}
                  <span className="text-green-600 font-semibold">
                    ✅ {boqStageGroups.reduce((s,g)=>s+g.rows.filter(r=>r.matched).length,0)} matched
                  </span>
                  {" · "}
                  <span className="text-red-500 font-semibold">
                    ⚠️ {boqStageGroups.reduce((s,g)=>s+g.rows.filter(r=>!r.matched).length,0)} unmatched
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setBoqPreview(false); setBoqStageGroups([]); }}
                    className="px-4 py-2 rounded-xl border text-sm font-semibold text-gray-600">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveBOQ}
                    disabled={boqSaving}
                    className="px-6 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                    {boqSaving ? "Saving..." : "Save BOQ →"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}